import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Mail, Plus, Trash2, Loader, CheckCircle, AlertCircle, RefreshCw, Server, Lock, X, ShieldCheck } from 'lucide-react';
import { MailboxConnection, MailboxProvider, EncryptionMethod } from '../../types';
import { workspaceHeader } from '../../lib/workspaces';
import { confirmDialog } from '../../lib/confirmStore';

interface Sender {
  id: string;
  email: string;
  name: string;
  createdBy: string;
  createdAt: string;
}

async function getIdToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export default function EmailSenderManager() {
  const [activeTab, setActiveTab] = useState<'transactional' | 'personal'>('personal');
  const [senders, setSenders] = useState<Sender[]>([]);
  const [mailboxes, setMailboxes] = useState<MailboxConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Transactional form state
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [savingTransactional, setSavingTransactional] = useState(false);

  // Personal Mailbox Modal state
  const [showConnectModal, setShowShowConnectModal] = useState(false);
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null);

  const fetchSenders = useCallback(async () => {
    try {
      const token = await getIdToken();
      const res = await fetch('/api/email-senders', {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (res.ok) {
        setSenders(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch senders:', err);
    }
  }, []);

  const fetchMailboxes = useCallback(async () => {
    try {
      const token = await getIdToken();
      const res = await fetch('/api/mailboxes', {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (res.ok) {
        setMailboxes(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch mailboxes:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSenders(), fetchMailboxes()]);
    setLoading(false);
  }, [fetchSenders, fetchMailboxes]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAddTransactional = async () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSavingTransactional(true);
    setError('');
    setSuccess('');
    try {
      const token = await getIdToken();
      const res = await fetch('/api/email-senders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ email, name: newName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add sender');
      setNewEmail('');
      setNewName('');
      setSuccess(`Added verified sender ${email}`);
      setTimeout(() => setSuccess(''), 4000);
      await fetchSenders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add sender');
    } finally {
      setSavingTransactional(false);
    }
  };

  const handleDeleteTransactional = async (id: string, email: string) => {
    if (!(await confirmDialog(`Remove verified sender "${email}"?`))) return;
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/email-senders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (!res.ok) throw new Error('Delete failed');
      setSenders(prev => prev.filter(s => s.id !== id));
      setSuccess(`Removed ${email}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete sender');
    }
  };

  const handleTestMailbox = async (mailboxId: string) => {
    setTestingConnectionId(mailboxId);
    setError('');
    setSuccess('');
    try {
      const token = await getIdToken();
      const res = await fetch('/api/mailboxes/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ mailboxId }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || 'Connection test failed');
      setSuccess('✅ IMAP & SMTP connection verified successfully!');
      setTimeout(() => setSuccess(''), 4000);
      await fetchMailboxes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setTestingConnectionId(null);
    }
  };

  const handleDeleteMailbox = async (id: string, email: string) => {
    if (!(await confirmDialog(`Disconnect mailbox "${email}"? Synced messages will remain preserved in applicant history.`))) return;
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/mailboxes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (!res.ok) throw new Error('Disconnect failed');
      setMailboxes(prev => prev.filter(m => m.id !== id));
      setSuccess(`Disconnected ${email}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect mailbox');
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Tab Switcher */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setActiveTab('personal')}
            className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 ${
              activeTab === 'personal'
                ? 'bg-blue-50 text-brand border border-blue-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
          >
            <Server className="h-3.5 w-3.5" />
            <span>Personal Mailbox Connections</span>
            <span className="ml-1 px-1.5 py-0.2 text-[10px] bg-white border border-slate-200 rounded-full font-semibold">
              {mailboxes.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('transactional')}
            className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 ${
              activeTab === 'transactional'
                ? 'bg-blue-50 text-brand border border-blue-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
          >
            <Mail className="h-3.5 w-3.5" />
            <span>Verified Portal Senders</span>
            <span className="ml-1 px-1.5 py-0.2 text-[10px] bg-white border border-slate-200 rounded-full font-semibold">
              {senders.length}
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="p-1.5 text-slate-400 hover:text-brand transition-colors cursor-pointer"
          title="Refresh senders & mailboxes"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Global Feedback Messages */}
      {error && (
        <div className="flex items-center space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
          <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2.5 rounded-xl">
          <CheckCircle className="h-4 w-4 shrink-0" /><span>{success}</span>
        </div>
      )}

      {/* TAB 1: PERSONAL MAILBOX CONNECTIONS */}
      {activeTab === 'personal' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">
                Connect your personal Gmail, Outlook, or Custom SMTP/IMAP account to send emails and sync applicant conversations directly on candidate profiles.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowShowConnectModal(true)}
              className="px-3.5 py-2 btn-primary text-white text-xs font-bold rounded-xl flex items-center space-x-1.5 cursor-pointer transition-all shadow-sm shrink-0"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Connect Mailbox</span>
            </button>
          </div>

          {loading ? (
            <div className="py-8 text-center"><Loader className="animate-spin h-5 w-5 text-brand mx-auto" /></div>
          ) : mailboxes.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl py-8 text-center space-y-2">
              <Server className="h-7 w-7 text-slate-300 mx-auto" />
              <p className="text-xs font-bold text-slate-600">No personal mailbox connected yet</p>
              <p className="text-caption text-slate-400 max-w-sm mx-auto">
                Connect your work or personal email address so messages sent to applicants sync seamlessly into their profile timeline.
              </p>
              <button
                type="button"
                onClick={() => setShowShowConnectModal(true)}
                className="mt-2 px-3 py-1.5 text-xs font-bold text-brand bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all cursor-pointer"
              >
                + Connect Your Mailbox
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {mailboxes.map(m => (
                <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center justify-between gap-3">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                      <span className="text-xs font-bold text-slate-800 truncate">
                        {m.displayName ? `${m.displayName} <${m.email}>` : m.email}
                      </span>
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-100 text-slate-600 uppercase border border-slate-200">
                        {m.provider}
                      </span>
                      {m.status === 'active' ? (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center space-x-1">
                          <CheckCircle className="h-2.5 w-2.5 text-emerald-500" />
                          <span>Active & Synced</span>
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-rose-50 text-rose-700 border border-rose-200 flex items-center space-x-1">
                          <AlertCircle className="h-2.5 w-2.5 text-rose-500" />
                          <span>Reconnect Required</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3 text-[11px] text-slate-500 flex-wrap">
                      <span>IMAP: {m.imapHost}:{m.imapPort} ({m.imapSecurity})</span>
                      <span>SMTP: {m.smtpHost}:{m.smtpPort} ({m.smtpSecurity})</span>
                      {m.lastTestedAt && (
                        <span>Tested: {new Date(m.lastTestedAt).toLocaleString()}</span>
                      )}
                    </div>
                    {m.lastError && (
                      <p className="text-[11px] text-rose-600 font-medium">{m.lastError}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleTestMailbox(m.id)}
                      disabled={testingConnectionId === m.id}
                      className="px-2.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-medium flex items-center space-x-1 cursor-pointer transition-all disabled:opacity-50"
                      title="Test IMAP & SMTP credentials"
                    >
                      {testingConnectionId === m.id ? (
                        <Loader className="h-3 w-3 animate-spin text-brand" />
                      ) : (
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                      )}
                      <span>Test</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteMailbox(m.id, m.email)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                      title="Disconnect mailbox"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: VERIFIED PORTAL SENDERS */}
      {activeTab === 'transactional' && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Add sender email addresses verified under your workspace domain (Resend/Transactional).
          </p>

          <div className="flex items-center space-x-2">
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="sender@example.com"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400"
            />
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Sender Name (optional)"
              className="w-48 px-3 py-2 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400"
            />
            <button
              type="button"
              onClick={handleAddTransactional}
              disabled={savingTransactional || !newEmail.trim()}
              className="px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl flex items-center space-x-1.5 cursor-pointer transition-all shadow-sm"
            >
              {savingTransactional ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              <span>Add Sender</span>
            </button>
          </div>

          {loading ? (
            <div className="py-6 text-center"><Loader className="animate-spin h-5 w-5 text-brand mx-auto" /></div>
          ) : senders.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl py-8 text-center space-y-1">
              <Mail className="h-6 w-6 text-slate-300 mx-auto" />
              <p className="text-sm font-bold text-slate-400">No verified senders added yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {senders.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span className="text-xs font-bold text-slate-700">
                      {s.name ? `${s.name} <${s.email}>` : s.email}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteTransactional(s.id, s.email)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                    title="Remove sender"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CONNECT MAILBOX MODAL */}
      {showConnectModal && (
        <ConnectMailboxModal
          onClose={() => setShowShowConnectModal(false)}
          onConnected={async () => {
            setShowShowConnectModal(false);
            setSuccess('Mailbox connected successfully!');
            setTimeout(() => setSuccess(''), 4000);
            await fetchMailboxes();
          }}
        />
      )}
    </div>
  );
}

// ── Connect Mailbox Guided Setup Modal ──

interface ConnectModalProps {
  onClose: () => void;
  onConnected: () => void;
}

function ConnectMailboxModal({ onClose, onConnected }: ConnectModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [provider, setProvider] = useState<MailboxProvider>('custom');

  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapSecurity, setImapSecurity] = useState<EncryptionMethod>('SSL/TLS');

  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpSecurity, setSmtpSecurity] = useState<EncryptionMethod>('STARTTLS');

  const [username, setUsername] = useState('');

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');

  // Auto-preset handler based on provider or email change
  const applyPreset = useCallback((p: MailboxProvider, currentEmail: string, region: string = 'com', plan: 'free' | 'paid' = 'free') => {
    setProvider(p);
    if (p === 'gmail') {
      setImapHost('imap.gmail.com');
      setImapPort(993);
      setImapSecurity('SSL/TLS');
      setSmtpHost('smtp.gmail.com');
      setSmtpPort(465);
      setSmtpSecurity('SSL/TLS');
    } else if (p === 'outlook') {
      setImapHost('outlook.office365.com');
      setImapPort(993);
      setImapSecurity('SSL/TLS');
      setSmtpHost('smtp.office365.com');
      setSmtpPort(587);
      setSmtpSecurity('STARTTLS');
    } else if (p === 'zoho') {
      const ext = region === 'eu' ? 'zoho.eu' : region === 'in' ? 'zoho.in' : region === 'au' ? 'zoho.com.au' : 'zoho.com';
      // Zoho publishes different server names for free and paid mailboxes.
      // Keep this choice explicit: a custom-domain address is not sufficient
      // to infer the plan, and the wrong flavor rejects otherwise-valid app
      // passwords.
      const imapPrefix = plan === 'paid' ? 'imappro' : 'imap';
      const smtpPrefix = plan === 'paid' ? 'smtppro' : 'smtp';
      setImapHost(`${imapPrefix}.${ext}`);
      setImapPort(993);
      setImapSecurity('SSL/TLS');
      setSmtpHost(`${smtpPrefix}.${ext}`);
      setSmtpPort(465);
      setSmtpSecurity('SSL/TLS');
    }
  }, []);

  const handleEmailChange = (val: string) => {
    setEmail(val);
    if (!username) setUsername(val);
    const domain = (val.split('@')[1] || '').toLowerCase();
    if (domain === 'gmail.com' || domain.endsWith('.gmail.com')) {
      applyPreset('gmail', val);
    } else if (domain === 'outlook.com' || domain === 'hotmail.com' || domain.includes('office365')) {
      applyPreset('outlook', val);
    } else if (domain.includes('zoho')) {
      if (domain.endsWith('.eu')) applyPreset('zoho', val, 'eu');
      else if (domain.endsWith('.in')) applyPreset('zoho', val, 'in');
      else if (domain.endsWith('.com.au')) applyPreset('zoho', val, 'au');
      else applyPreset('zoho', val, 'com');
    }
  };

  const handleTest = async () => {
    if (!email || !password || !imapHost || !smtpHost) {
      setModalError('Please fill in email, password, IMAP host, and SMTP host before testing.');
      return;
    }
    setTesting(true);
    setModalError('');
    setModalSuccess('');
    try {
      const token = await getIdToken();
      const res = await fetch('/api/mailboxes/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({
          imapHost, imapPort, imapSecurity,
          smtpHost, smtpPort, smtpSecurity,
          username: username || email, password,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        let msg = data.error || 'Connection test failed';
        const p = data.protocols;
        if (p?.smtp || p?.imap) {
          const lines: string[] = [];
          if (p.smtp?.ok) lines.push('✅ SMTP — connected and authenticated');
          else lines.push(`❌ SMTP — ${p.smtp?.error || 'failed'}`);
          if (p.imap?.ok) lines.push('✅ IMAP — connected and authenticated');
          else lines.push(`❌ IMAP — ${p.imap?.error || 'failed'}`);
          msg = lines.join('\n\n');
          if (p.imap?.ok === false && p.smtp?.ok === true && (provider === 'zoho' || imapHost.toLowerCase().includes('zoho') || smtpHost.toLowerCase().includes('zoho'))) {
            msg += '\n\n💡 SMTP works, so your credentials are valid — Zoho is refusing IMAP specifically. The #1 cause is IMAP Access being disabled for this mailbox: open Zoho Mail → gear icon → Mail Accounts → enable “IMAP Access” (Workplace accounts may need the org admin to allow IMAP in the Admin Console).';
          }
        }
        throw new Error(msg);
      }
      const protos = data.protocols;
      const hosts = protos
        ? `\n\n📥 IMAP: ${protos.imap?.host || ''}\n📤 SMTP: ${protos.smtp?.host || ''}`
        : '';
      setModalSuccess(`✅ Connection Test Successful! IMAP login and SMTP authentication verified.${hosts}`);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!email || !password) {
      setModalError('Email address and Password / App Password are required.');
      return;
    }
    setSaving(true);
    setModalError('');
    setModalSuccess('');
    try {
      const token = await getIdToken();
      const res = await fetch('/api/mailboxes/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({
          email: email.trim(),
          displayName: displayName.trim(),
          provider,
          imapHost, imapPort, imapSecurity,
          smtpHost, smtpPort, smtpSecurity,
          username: (username || email).trim(),
          password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to connect mailbox');
      onConnected();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to connect mailbox');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-2">
            <Server className="h-5 w-5 text-brand" />
            <h3 className="text-sm font-bold text-slate-800">Connect Your Personal Mailbox</h3>
          </div>
          <button aria-label="Close" title="Close" type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3 flex items-start space-x-2.5">
            <Lock className="h-4 w-4 text-brand shrink-0 mt-0.5" />
            <p className="text-caption text-slate-600 leading-relaxed">
              Your password or App Password is encrypted at rest using AES-256-GCM. We only perform applicant-scoped thread lookups on profile views and never sync your entire mailbox.
            </p>
          </div>

          {/* Email & Display Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                placeholder="john@agency.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Display Name (optional)</label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
            </div>
          </div>

          {/* Password / App Password */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Password or App-Specific Password *</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••••••••••"
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              For Gmail, Office365 and Zoho, generate an <strong>App Password</strong> in your email account's security settings whenever 2-step verification (2FA/SSO) is on — your normal login password will be rejected by the provider.
            </p>
          </div>

          {/* Provider Preset Picker */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Provider Presets</label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => applyPreset('gmail', email)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'gmail' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                GMAIL
              </button>
              <button
                type="button"
                onClick={() => applyPreset('outlook', email)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'outlook' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                OUTLOOK
              </button>
              <button
                type="button"
                onClick={() => applyPreset('zoho', email, 'com', 'free')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'zoho' && imapHost === 'imap.zoho.com' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ZOHO US (FREE)
              </button>
              <button
                type="button"
                onClick={() => applyPreset('zoho', email, 'com', 'paid')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'zoho' && imapHost === 'imappro.zoho.com' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ZOHO US (PAID)
              </button>
              <button
                type="button"
                onClick={() => applyPreset('zoho', email, 'eu', 'free')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'zoho' && imapHost === 'imap.zoho.eu' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ZOHO (EU)
              </button>
              <button
                type="button"
                onClick={() => applyPreset('zoho', email, 'in', 'free')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'zoho' && imapHost === 'imap.zoho.in' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ZOHO (IN)
              </button>
              <button
                type="button"
                onClick={() => applyPreset('zoho', email, 'au', 'free')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'zoho' && imapHost === 'imap.zoho.com.au' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ZOHO (AU)
              </button>
              <button
                type="button"
                onClick={() => { setProvider('custom'); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer transition-all ${
                  provider === 'custom' ? 'bg-blue-50 text-brand border-blue-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                CUSTOM
              </button>
            </div>
          </div>

          {/* Provider Specific Helper Banners */}
          {(provider === 'zoho' || imapHost.toLowerCase().includes('zoho') || smtpHost.toLowerCase().includes('zoho')) && (
            <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3 space-y-1 text-slate-700 text-[11px] leading-relaxed">
              <p className="font-bold text-amber-800">Zoho Mail Requirements:</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900">
                <li>IMAP Access MUST be enabled in Zoho Mail Settings (gear icon &gt; Mail Accounts &gt; IMAP Access). For Zoho Workplace / paid orgs an admin may need to allow IMAP in the Admin Console.</li>
                <li>If 2-Factor Authentication (or SAML/SSO) is enabled, generate a <strong>Zoho App Password</strong> under accounts.zoho.com &gt; Security &gt; App Passwords and use it here — Zoho rejects your normal login password in external clients.</li>
                <li>Select <strong>ZOHO US (PAID)</strong> for a paid Zoho Workplace/custom-domain mailbox: <code className="font-mono bg-amber-100 px-1 rounded">imappro.zoho.com</code> / <code className="font-mono bg-amber-100 px-1 rounded">smtppro.zoho.com</code>. Select <strong>ZOHO US (FREE)</strong> for a personal <code className="font-mono bg-amber-100 px-1 rounded">@zohomail.com</code> or free-plan mailbox: <code className="font-mono bg-amber-100 px-1 rounded">imap.zoho.com</code> / <code className="font-mono bg-amber-100 px-1 rounded">smtp.zoho.com</code>. Use the matching regional suffix for EU, India, or Australia; Zoho Mail Settings &gt; Mail Accounts &gt; your address &gt; Server Details is authoritative.</li>
                <li>Zoho's security type <strong>&quot;SSL&quot;</strong> is the <strong>&quot;SSL (Implicit TLS)&quot;</strong> option in the fields below (IMAP port 993 / SMTP port 465). Port 587 + STARTTLS also works.</li>
              </ul>
            </div>
          )}

          {provider === 'gmail' && (
            <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 space-y-1 text-slate-700 text-[11px] leading-relaxed">
              <p className="font-bold text-blue-800">Gmail Requirements:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-900">
                <li>Generate a 16-character <strong>App Password</strong> in Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords.</li>
                <li>Enable IMAP in Gmail Settings &gt; See all settings &gt; Forwarding and POP/IMAP &gt; Enable IMAP.</li>
              </ul>
            </div>
          )}

          {/* IMAP Settings */}
          <div className="border-t border-slate-100 pt-3 space-y-2">
            <h4 className="text-xs font-bold text-slate-800">Receiving Settings (IMAP)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-medium text-slate-500 mb-1">IMAP Host</label>
                <input
                  type="text"
                  value={imapHost}
                  onChange={e => setImapHost(e.target.value)}
                  placeholder="imap.example.com"
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-medium"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Port</label>
                <input
                  type="number"
                  value={imapPort}
                  onChange={e => setImapPort(Number(e.target.value))}
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-mono"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Security / SSL</label>
                <select
                  value={imapSecurity}
                  onChange={e => {
                    const sec = e.target.value as EncryptionMethod;
                    setImapSecurity(sec);
                    if (sec === 'SSL/TLS') setImapPort(993);
                    else if (sec === 'STARTTLS' || sec === 'None') setImapPort(143);
                  }}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-medium outline-none cursor-pointer"
                >
                  <option value="SSL/TLS">SSL (Implicit TLS) — Port 993</option>
                  <option value="STARTTLS">STARTTLS (Port 143)</option>
                  <option value="None">None (Insecure)</option>
                </select>
              </div>
            </div>
          </div>

          {/* SMTP Settings */}
          <div className="border-t border-slate-100 pt-3 space-y-2">
            <h4 className="text-xs font-bold text-slate-800">Sending Settings (SMTP)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-medium text-slate-500 mb-1">SMTP Host</label>
                <input
                  type="text"
                  value={smtpHost}
                  onChange={e => setSmtpHost(e.target.value)}
                  placeholder="smtp.example.com"
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-medium"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Port</label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={e => setSmtpPort(Number(e.target.value))}
                  className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-mono"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Security / SSL</label>
                <select
                  value={smtpSecurity}
                  onChange={e => {
                    const sec = e.target.value as EncryptionMethod;
                    setSmtpSecurity(sec);
                    if (sec === 'SSL/TLS') setSmtpPort(465);
                    else if (sec === 'STARTTLS') setSmtpPort(587);
                    else if (sec === 'None') setSmtpPort(25);
                  }}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white font-medium outline-none cursor-pointer"
                >
                  <option value="SSL/TLS">SSL (Implicit TLS) — Port 465</option>
                  <option value="STARTTLS">STARTTLS (Port 587)</option>
                  <option value="None">None (Port 25/587)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Feedback */}
          {modalError && (
            <div className="flex items-start space-x-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3.5 py-2 rounded-xl">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span className="whitespace-pre-line leading-relaxed">{modalError}</span>
            </div>
          )}
          {modalSuccess && (
            <div className="flex items-start space-x-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3.5 py-2 rounded-xl">
              <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" /><span className="whitespace-pre-line leading-relaxed">{modalSuccess}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || saving}
              className="px-3.5 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl flex items-center space-x-1.5 cursor-pointer disabled:opacity-50"
            >
              {testing ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
              <span>Test Connection</span>
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || testing}
                className="px-5 py-2 btn-primary text-white text-xs font-bold rounded-xl flex items-center space-x-1.5 cursor-pointer shadow-sm disabled:opacity-50"
              >
                {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                <span>Connect & Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
